// import { RequestsComponent } from '@clooper/common';
import { Injectable } from '@angular/core';
import { HttpResponse, HttpRequest } from '@angular/common/http';

/**
* @interface CachedResponse
* to describe cache response.
* @member {string} url  of the request that is cached.
* @member {HttpResponse<any>} response response of the http request to be cached.
* @member {any} expires time the cached response expires
*/
export interface CachedResponse {
  url: string;
  response: HttpResponse<any>;
  expires?: any;
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {

  /**
  * cache is a class property used to store cache responses.
  * @type {Map<string, CachedResponse>}
  * @private
  */
  private cache = new Map<string, CachedResponse>();


  /**
  * Gets response of a Http request from cache or returns null.
  *
  * @param { string } prefix The domain (base url) of the request.
  * @param { HttpRequest<any>} req The cached request.
  * @return {HttpResponse<any> | null} Returns a HttpResponse.
  */
  get(prefix: string, req: HttpRequest<any>): HttpResponse<any> | null {
    const url = prefix + ':' + req.urlWithParams;
    const cached = this.cache.get(url);

    if (!cached) {
      return null;
    }

    const { expires, response } = cached;

    // Don't observe expired keys
    const now = new Date();
    if (expires && expires.getTime() < now.getTime()) {
      this.cache.delete(url);
      return null;
    }

    return response;
  }

  /**
  * Put response of a Http request into cache.
  *
  * @param { string } prefix The domain (base url) of the request.
  * @param { HttpRequest<any>} req The request to be put in cache.
  * @param { HttpResponse<any>} response The response of the request.
  * @param { any } ttl The cache time to live.
  */
  put(prefix: string, req: HttpRequest<any>, response: HttpResponse<any>, ttl?: any) {
    const url = prefix + ':' + req.urlWithParams;
    if (ttl) {
      const expires = new Date();
      expires.setSeconds(expires.getSeconds() + ttl);
      this.cache.set(url, { url, expires, response });
    } else {
      this.cache.set(url, { url, response });
    }
    this.deleteExpiredEntries();
  }

  /**
  * Delete expired entries from cache
  *
  */
  private deleteExpiredEntries() {
    this.cache.forEach(entry => {
      const { url, expires } = entry;
      const now = new Date();
      if (expires && expires.getTime() < now.getTime()) {
        this.cache.delete(url);
      }
    });
  }

  /**
  * Remove a cache response using url as key.
  * @param { string } key The url of the cache request.
  */
  remove(key: string) {
    this.cache.forEach(entry => {
      const { url } = entry;
      if (url.match(key)) {
        this.cache.delete(url);
      }
    });
  }

  /**
  * Clears cache
  */
  clear() {
    this.cache.clear();
  }

set(key: string, value: any, ttlSeconds?: number): void {
    // your implementation (localStorage, Map, IndexedDB, etc.)
    localStorage.setItem(key, JSON.stringify(value));
    // or if using a Map: this.cacheMap.set(key, value);
  }
}
